import React, { useEffect, useState } from "react";

import Table from "../../components/Table";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
} from "@mui/material";
import { toRegulerCase } from "../../utils/Utils";
import { handleDjangoDataRequest } from "../../utils/apiUtils";
import TableSkeleton from "../../components/TableSkeleton";

import moment from "moment";
import Nodata from "../../components/Nodata";

const toUtcParts = (dateStr, timeStr) => {
  const local = new Date(`${dateStr}T${timeStr}:00`);
  const iso = local.toISOString();
  return { date: iso.slice(0, 10), time: iso.slice(11, 16) };
};

const formatDateParam = (dateStr) => {
  const d = moment(dateStr, "YYYY-MM-DD");
  return `${d.format("MMMM")}%20${d.format("D")}%2C%20${d.format("YYYY")}`;
};

export default function AdvanceCmLog({ hotelId, isNewReport }) {
  const [rowData, setRowData] = useState();

  const [columnDefs, setColumnDefs] = useState();
  const handleColumn = (data) => {
    if (data) {
      const culumnData = Object.keys(data[0]).map((elm) => ({
        field: elm,
        headerName: toRegulerCase(elm),
      }));
      setColumnDefs(culumnData);
    }
  };

  //-------------------
  const [selectedDate, setSelectedDate] = useState(
    moment().format("YYYY-MM-DD"),
  );
  const [startTime, setStartTime] = useState("00:00");
  const [endTime, setEndTime] = useState("23:59");
  const [useLocal, setUseLocal] = useState(true);
  const [filterByOperationDate, setFilterByOperationDate] = useState(true);
  const [sources, setSources] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(true);
  const [selectedSource, setSelectedSource] = useState(null);
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    if (hotelId) {
      setSourcesLoading(true);
      handleDjangoDataRequest(
        `api/v2/metaAdmin/metaAdvanceCMLogsSourcesForHotel/?hotel_id=${hotelId}`,
      )
        .then((data) => {
          setSources(data || []);
          setSourcesLoading(false);
        })
        .catch(() => {
          setSources([]);
          setSourcesLoading(false);
        });
    }
  }, [hotelId]);

  const [loading, setLoading] = useState(false);
  const [queried, setQueried] = useState(false);
  const handleShowButton = () => {
    const url = isNewReport
      ? "metaAdvanceCMLogsForHotel"
      : "metaOldAdvanceCMLogsForHotel";
    setLoading(true);
    setQueried(true);

    let utcFromDate, utcToDate, utcStart, utcEnd;
    if (filterByOperationDate) {
      // Operation date mode: full day in UTC, no timezone conversion needed
      utcFromDate = selectedDate;
      utcToDate = selectedDate;
      utcStart = "00:00";
      utcEnd = "23:59";
    } else if (useLocal) {
      const from = toUtcParts(selectedDate, startTime);
      const to = toUtcParts(selectedDate, endTime);
      utcFromDate = from.date;
      utcToDate = to.date;
      utcStart = from.time;
      utcEnd = to.time;
    } else {
      utcFromDate = selectedDate;
      utcToDate = selectedDate;
      utcStart = startTime;
      utcEnd = endTime;
    }

    const isUpdateTime = filterByOperationDate ? "false" : "true";
    const daterange = `${formatDateParam(utcFromDate)}%20-%20${formatDateParam(utcToDate)}`;
    const sourceParam = selectedSource
      ? `&source=${encodeURIComponent(selectedSource)}`
      : "";
    handleDjangoDataRequest(
      `api/v2/metaAdmin/${url}/?hotel_id=${hotelId}&daterange=${daterange}&start_time=${utcStart}&end_time=${utcEnd}&is_update_time=${isUpdateTime}${sourceParam}`,
    )
      .then((data) => {
        setLoading(false);
        handleColumn(data);
        setRowData(data);
      })
      .catch((err) => setLoading(false));
  };

  return (
    <>
      <Alert severity="info" sx={{ mb: 1 }}>
        {filterByOperationDate
          ? `Operation Date — "What changes were made FOR this date?" Example: Select May 10 to see all rate and inventory updates that apply to May 10, regardless of when those changes were made. You can select future dates. Turn off the "Operation Date" toggle to switch to Update Time mode.`
          : `Update Time — "What changes were made ON this date?" Example: Select April 5 to see all updates that were pushed on April 5, regardless of which dates those changes apply to. Only past dates can be selected. Turn on the "Operation Date" toggle to switch back. ${useLocal ? `Times shown in ${browserTz}.` : "Times shown in UTC."}`}
      </Alert>
      <Alert severity="warning" sx={{ mb: 1 }}>
        Please select a Source (Channel) (e.g. booking, expedia, goibibo) from
        the dropdown to narrow your results. Without a source (channel) filter,
        the query may return a large number of records and take longer to load.
      </Alert>
      <Box sx={{ display: "flex", marginBottom: 1, alignItems: "center" }}>
        <FormControlLabel
          control={
            <Switch
              checked={filterByOperationDate}
              onChange={(e) => setFilterByOperationDate(e.target.checked)}
              size="small"
            />
          }
          label={filterByOperationDate ? "Operation Date" : "Update Time"}
          sx={{ mr: 1, minWidth: 150 }}
        />
        {!filterByOperationDate && (
          <FormControlLabel
            control={
              <Switch
                checked={useLocal}
                onChange={(e) => setUseLocal(e.target.checked)}
                size="small"
              />
            }
            label={useLocal ? browserTz : "UTC"}
            sx={{ mr: 1, minWidth: 140 }}
          />
        )}
        <TextField
          label="Date"
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
          inputProps={
            filterByOperationDate ? {} : { max: moment().format("YYYY-MM-DD") }
          }
          sx={{ width: 160 }}
        />
        <Autocomplete
          options={sources}
          value={selectedSource}
          onChange={(e, val) => setSelectedSource(val)}
          loading={sourcesLoading}
          disabled={sourcesLoading}
          size="small"
          sx={{ ml: 1, width: 220 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                sourcesLoading ? "Loading sources..." : "Source / Channel (All)"
              }
              InputLabelProps={{ shrink: true }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {sourcesLoading ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {!filterByOperationDate && (
          <>
            <TextField
              label={`Start Time (${useLocal ? browserTz : "UTC"})`}
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 60 }}
              sx={{ ml: 1, width: 160 }}
            />
            <TextField
              label={`End Time (${useLocal ? browserTz : "UTC"})`}
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 60 }}
              sx={{ ml: 1, width: 160 }}
            />
          </>
        )}
        <Button
          variant="custom-button"
          sx={{ ml: 2 }}
          onClick={handleShowButton}
          disabled={sourcesLoading}
        >
          {sourcesLoading ? "Loading sources..." : "Show Data"}
        </Button>
      </Box>
      {loading ? (
        <TableSkeleton />
      ) : queried ? (
        rowData ? (
          <Table rowData={rowData} columnDefs={columnDefs} />
        ) : (
          <Nodata />
        )
      ) : null}
    </>
  );
}
